import { z } from 'zod';

/**
 * Daemon (Workforce employee) create / update payload.
 *
 * Single source of truth per ENTITY_CREATION_WIZARD_SPEC.md §3.1 — imported by
 *   server: `server/src/api/routes/workforce.ts` (POST + PATCH validation)
 *   client: `client/src/pages/admin/daemon/NewDaemonPage.tsx` (RHF resolver)
 *           per-step `trigger(stepFields)` runs against this schema.
 *           `{Entity}Assistant` includes this schema in the system prompt.
 *
 * Draft vs active is NOT a schema split (§3.4). Only `name` is required at the
 * schema level. The richer set of fields required for `status='active'` is
 * enforced by `canActivate()` below — checked by both the wizard client-side
 * (to disable the Activate button) and the server (to enforce).
 */
export const DaemonSchema = z.object({
  // Identity
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers, and hyphens only')
    .optional(),
  name: z.string().min(1, 'Name is required'),
  department: z.enum(
    ['research', 'security', 'quality', 'testing', 'docs', 'performance', 'infrastructure', 'custom'],
    {
      message: 'Department is required',
    },
  ),
  type: z.enum(['guardian', 'sentinel', 'operative']).optional(),
  mandate: z.string().optional(),

  // Qualifications
  templateId: z.number().int().positive().nullable().optional(),
  permissionTier: z.enum(['observer', 'contributor', 'operator']).optional(),
  requiresApproval: z.boolean().optional(),

  // Workflows
  workflowDefinitionId: z.number().int().positive().nullable().optional(),

  // Triggers
  triggers: z
    .array(
      z.object({
        type: z.enum(['cron', 'event', 'chained', 'manual']),
        cron: z.string().optional(),
        eventPattern: z.string().optional(),
        eventFilter: z.record(z.string(), z.unknown()).optional(),
        chainDaemonId: z.number().int().positive().optional(),
      }),
    )
    .optional(),

  // Supervision
  supervisorDaemonId: z.number().int().positive().nullable().optional(),
  probationDays: z.number().int().positive().nullable().optional(),

  // Lifecycle (server authoritative, but wizard sends on save/activate)
  lifecycleStatus: z.enum(['draft', 'active', 'inactive', 'archived']).optional(),

  // Marketplace provenance — carried through when installed via 'Customize before install' path.
  marketplaceSourceId: z.number().int().positive().nullable().optional(),
});

export type DaemonInput = z.infer<typeof DaemonSchema>;

/**
 * Per-step field lists used by the wizard's step indicator + validation.
 * Keep in sync with the 5 HireStep* components' field contents.
 */
export const DAEMON_WIZARD_STEPS = [
  { key: 'identity', label: 'Identity', fields: ['slug', 'name', 'department', 'type', 'mandate'] },
  {
    key: 'qualifications',
    label: 'Qualifications',
    fields: ['templateId', 'permissionTier', 'requiresApproval'],
  },
  { key: 'workflows', label: 'Workflows', fields: ['workflowDefinitionId'] },
  { key: 'triggers', label: 'Triggers', fields: ['triggers'] },
  { key: 'supervision', label: 'Supervision', fields: ['supervisorDaemonId', 'probationDays'] },
] as const satisfies ReadonlyArray<{
  key: string;
  label: string;
  fields: ReadonlyArray<keyof DaemonInput>;
}>;

export type DaemonWizardStepKey = (typeof DAEMON_WIZARD_STEPS)[number]['key'];

/**
 * Business-layer activation gate (spec §3.4). Called by both the wizard's
 * client-side activate-button disabler AND the server's
 * `setLifecycleStatus('active')` handler so both sides enforce the same rules.
 *
 * Returns the list of missing-but-required-for-active fields; empty array =
 * activation is allowed.
 */
export interface ActivationCheck {
  ok: boolean;
  missing: ReadonlyArray<keyof DaemonInput>;
}

export function canActivateDaemon(input: DaemonInput): ActivationCheck {
  const missing: Array<keyof DaemonInput> = [];
  if (!input.name?.trim()) missing.push('name');
  if (!input.department) missing.push('department');
  if (!input.type) missing.push('type');
  if (!input.permissionTier) missing.push('permissionTier');
  // A daemon that runs autonomously needs at least ONE trigger OR a linked
  // workflow (the workflow can be triggered externally). No trigger and no
  // workflow = the daemon can never run — bad activation.
  const hasTrigger = (input.triggers?.length ?? 0) > 0;
  const hasWorkflow = input.workflowDefinitionId != null;
  if (!hasTrigger && !hasWorkflow) {
    missing.push('triggers');
  }
  return { ok: missing.length === 0, missing };
}
